import React from 'react';
import {Modal, Typography, Button, Collapse, Skeleton, Timeline, Row, Col} from 'antd';
import {ClockCircleOutlined, HourglassOutlined} from '@ant-design/icons';
import UploadComponent from './UploadComponent';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const {Title, Paragraph} = Typography;
const {Panel} = Collapse;


const getOrderSource = (entryUser) => {
    if (entryUser.startsWith('CS:')) return 'client service';
    if (entryUser.startsWith('LOG:')) return 'old logistic';
    if (entryUser.startsWith('W:')) return 'welcome';
    if (entryUser === 'user') return 'auto-generated';
    return entryUser;
};

const DetailsModal = ({isModalVisible, selectedRowData, handleModalClose, token}) => {
    const formatDateToNY = (date) => date ? dayjs(date).tz('America/New_York').format('MM/DD/YYYY hh:mm:ss A') : 'TBD';

    // Generate the timeline items based on the order data
    const generateTimelineItems = (order) => {
        const items = [
            {
                children: <><strong>Order Created:</strong> {formatDateToNY(order.created)}</>,
            },
            {
                children: order.picked_up_date
                    ? <><strong>Driver Picked Up Speciaments:</strong> {formatDateToNY(order.picked_up_date)}</>
                    : 'Waiting for Order Pickup',
                dot: !order.picked_up_date ? <HourglassOutlined/> : null,
                color: !order.picked_up_date ? 'orange' : 'blue',
            },
            {
                children: order.handedOverAt
                    ? <><strong>Handed Over to the ECL Accession Dept:</strong> {formatDateToNY(order.handedOverAt)}</>
                    : 'Waiting for Handover',
                dot: !order.handedOverAt ? <HourglassOutlined/> : null,
                color: !order.handedOverAt ? 'orange' : 'blue',
            },
            {
                children: order.receivedAt
                    ? <><strong>Received and Confirmed:</strong> {formatDateToNY(order.receivedAt)}</>
                    : 'Waiting for Receipt',
                dot: !order.receivedAt ? <HourglassOutlined/> : null,
                color: !order.receivedAt ? 'orange' : 'blue',
            },
        ];

        if (order.status === 'RECEIVED_WITH_ISSUE' && order.receivedIssueWasFixedAt) {
            items.push({
                dot: <ClockCircleOutlined className="timeline-clock-icon"/>,
                color: 'red',
                children: `Issue Fixed: ${formatDateToNY(order.receivedIssueWasFixedAt)}`,
            });
        } else if (order.status === 'RECEIVED_WITH_ISSUE' && !order.receivedIssueWasFixedAt) {
            items.push({
                dot: <HourglassOutlined/>,
                color: 'orange',
                children: 'Waiting for Issue to be Fixed',
            });
        }

        return items;
    };

    return (
        <Modal
            title={<Title level={3}>Order Details</Title>}
            open={isModalVisible}
            onCancel={handleModalClose}
            closable={false}
            maskClosable={false}
            footer={[
                <Button key="close" onClick={handleModalClose}>
                    Close
                </Button>
            ]}
            width={window.innerWidth > 768 ? '70%' : '90%'}
        >
            {selectedRowData ? (
                <Collapse defaultActiveKey={['1']} accordion>
                    <Panel header="General Information" key="1">
                        <Skeleton loading={!selectedRowData} active>
                            <Row>


                                <Col span={14}>
                                    <p><strong>Quantity:</strong> {selectedRowData.quantity ?? 'N/A'}</p>
                                    <p><strong>ID:</strong> {selectedRowData.id || 'N/A'}</p>
                                    <p><strong>Confirmation ID:</strong> {selectedRowData.confirmationId || 'N/A'}</p>
                                    <p><strong>Type:</strong> {selectedRowData.type || 'N/A'}</p>
                                    <p><strong>Status:</strong> {selectedRowData.status || 'N/A'}</p>
                                    <p><strong>Notes:</strong> {selectedRowData.notes || 'N/A'}</p>
                                    <p><strong>Order source:</strong>&nbsp;
                                        {getOrderSource(selectedRowData.entryUser)}
                                    </p>
                                    <p><strong>Received Issue Notes:</strong> {selectedRowData.receiverNotes || 'N/A'}
                                    </p>
                                </Col>
                                <Col span={10}>
                                    <Timeline items={generateTimelineItems(selectedRowData)} mode={"right"}/>
                                </Col>
                            </Row>


                            {/* Render the Timeline */}

                        </Skeleton>
                    </Panel>
                    <Panel header="Route Details" key="2">
                        <Skeleton loading={!selectedRowData} active>
                            <p><strong>Route Code:</strong> {selectedRowData.routeDetails?.code || 'N/A'}</p>
                        </Skeleton>
                    </Panel>
                    <Panel header="Driver Details" key="3">
                        <Skeleton loading={!selectedRowData} active>
                            <p><strong>Driver First Name:</strong> {selectedRowData.driverDetails?.first_name || 'N/A'}
                            </p>
                            <p><strong>Driver Last Name:</strong> {selectedRowData.driverDetails?.last_name || 'N/A'}
                            </p>
                            <p><strong>Driver Phone:</strong> {selectedRowData.driverDetails?.phone || 'N/A'}</p>
                        </Skeleton>
                    </Panel>
                    <Panel header="Client Details" key="4">
                        <Skeleton loading={!selectedRowData} active>
                            <p><strong>Client ID:</strong> {selectedRowData.clientDetails?.id || 'N/A'}</p>
                            <p><strong>Client Name:</strong> {selectedRowData.clientDetails?.name || 'N/A'}</p>
                            <p><strong>Client City:</strong> {selectedRowData.clientDetails?.city || 'N/A'}</p>
                            <p><strong>Client State:</strong> {selectedRowData.clientDetails?.state || 'N/A'}</p>
                            <p><strong>Client Zip:</strong> {selectedRowData.clientDetails?.zip || 'N/A'}</p>
                            <p><strong>Client Street:</strong> {selectedRowData.clientDetails?.street || 'N/A'}</p>
                        </Skeleton>
                    </Panel>
                    <Panel header="Photos" key="5">
                        <UploadComponent
                            token={token}
                            currentOrderId={selectedRowData.id}
                            displayOnly={true} // Display-only mode
                        />
                    </Panel>
                </Collapse>
            ) : (
                <Paragraph>No data available.</Paragraph>
            )}
        </Modal>
    );
}

export default DetailsModal;
